type IconKind = "control" | "stealth" | "command" | "play" | "download" | "edit" | "delete" | "details" | "enable" | "disable" | "members" | "approve";

export type ChromeNavIconKind = "devices" | "recordings" | "audit" | "settings";

const strokeIcon = {
  fill: "none" as const,
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true as const,
};

function ActionIcon(props: { kind: IconKind }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    ...strokeIcon,
    strokeWidth: 1.8,
  };
  if (props.kind === "control") {
    return (
      <svg {...common}>
        <rect x="3" y="4" width="18" height="12" rx="1.5" />
        <path d="M8 20h8" />
        <path d="M12 16v4" />
      </svg>
    );
  }
  if (props.kind === "stealth") {
    return (
      <svg {...common}>
        <path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z" />
        <circle cx="12" cy="12" r="2.2" />
        <path d="M4 5l16 14" />
      </svg>
    );
  }
  if (props.kind === "command") {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="14" rx="1.5" />
        <path d="M7 10l3 2.5L7 15" />
        <path d="M12 15h5" />
      </svg>
    );
  }
  if (props.kind === "edit") {
    return (
      <svg {...common}>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
      </svg>
    );
  }
  if (props.kind === "delete") {
    return (
      <svg {...common}>
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2" />
        <path d="M6 6l1 14h10l1-14" />
      </svg>
    );
  }
  if (props.kind === "details") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 10v5" />
        <path d="M12 7h.01" />
      </svg>
    );
  }
  if (props.kind === "enable") {
    return (
      <svg {...common}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M19 8l2 2-4 4-2-2" />
      </svg>
    );
  }
  if (props.kind === "disable") {
    return (
      <svg {...common}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 7l-6 6" />
        <path d="M17 7l6 6" />
      </svg>
    );
  }
  if (props.kind === "members") {
    return (
      <svg {...common}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  if (props.kind === "approve") {
    return (
      <svg {...common}>
        <path d="M20 6L9 17l-5-5" />
      </svg>
    );
  }
  if (props.kind === "play") {
    return (
      <svg {...common}>
        <path d="M8 5v14l11-7z" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M12 3v12" />
      <path d="M8 11l4 4 4-4" />
      <path d="M4 19h16" />
    </svg>
  );
}

export function ChromeNavIcon(props: { kind: ChromeNavIconKind }) {
  const common = {
    className: "chrome-nav-icon",
    width: 15,
    height: 15,
    viewBox: "0 0 24 24",
    ...strokeIcon,
    strokeWidth: 2,
  };
  if (props.kind === "devices") {
    return (
      <svg {...common}>
        <rect x="3" y="4" width="18" height="12" rx="1.5" />
        <path d="M8 20h8" />
        <path d="M12 16v4" />
      </svg>
    );
  }
  if (props.kind === "recordings") {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="14" height="14" rx="1.5" />
        <path d="M17 9l4-2v10l-4-2V9z" />
      </svg>
    );
  }
  if (props.kind === "audit") {
    return (
      <svg {...common}>
        <path d="M8 6h13" />
        <path d="M8 12h13" />
        <path d="M8 18h9" />
        <path d="M4 6h.01" />
        <path d="M4 12h.01" />
        <path d="M4 18h.01" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    </svg>
  );
}

export function IconAction(props: { label: string; kind: IconKind; disabled?: boolean; title?: string; onClick: () => void }) {
  const title = props.title ?? props.label;
  return (
    <button type="button" className="icon-action" aria-label={props.label} title={title} disabled={props.disabled} onClick={props.onClick}>
      <ActionIcon kind={props.kind} />
    </button>
  );
}
